/**
 * Generic dashboard-callable invoker for the Subscriber Verification Console.
 *
 * Calls any client-functions callable by NAME STRING with `{ companyId }` as
 * the only payload. Returns `{ data, loading, error, refetch }`.
 *
 * Founder accounts bypass `enforceCompanyAccess` server-side (intentional
 * cross-tenant founder read; emits `emitFounderCrossTenantReadAudit`), so this
 * hook works for any subscriber companyId.
 *
 * NOTE on imports: we deliberately invoke by string name and type the result
 * as `unknown`. We do NOT import callable types from `services/client-functions`
 * because that would either cross workspace boundaries (CRA's
 * ModuleScopePlugin blocks it) or pull a heavy server module into the bundle.
 *
 * @see Plan: i-need-a-way-giggly-babbage.md (Wave 2 — Live Stats tab)
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

export interface UseDashboardCallableOptions {
  /** Skip the auto-fetch on mount. */
  manual?: boolean;
  /** Extra payload merged into `{ companyId }`. */
  extraPayload?: Record<string, unknown>;
}

export interface UseDashboardCallableReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  lastFetchedAt: Date | null;
}

export function useDashboardCallable<T>(
  callableName: string,
  companyId: string,
  options: UseDashboardCallableOptions = {}
): UseDashboardCallableReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const { manual, extraPayload } = options;

  const refetch = useCallback(async (): Promise<void> => {
    if (!companyId) {
      setError('companyId is required');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<Record<string, unknown>, T>(functions, callableName);
      const payload: Record<string, unknown> = { companyId, ...(extraPayload ?? {}) };
      const response = await fn(payload);
      setData(response.data);
      setLastFetchedAt(new Date());
    } catch (err) {
      const msg = err instanceof Error ? err.message : `${callableName} failed`;
      console.error(`[useDashboardCallable:${callableName}] error:`, err);
      setError(msg);
    } finally {
      setLoading(false);
    }
    // We intentionally do NOT depend on `extraPayload` identity to avoid
    // refetch loops when callers pass inline objects. Callers that need a
    // payload swap should call `refetch` explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callableName, companyId]);

  useEffect(() => {
    if (manual) return;
    void refetch();
  }, [manual, refetch]);

  return { data, loading, error, refetch, lastFetchedAt };
}
